import { Node, NodeProps, useReactFlow, useViewport } from '@xyflow/react';

import {
  SectionLabel,
  SectionLabelProps as SectionLabelPropsExternal,
} from '@/components/diagrams/components/SectionLabel';

import { isSectionLabelNode } from '../../utils/nodes';
import { isTextNoteNode } from '../../utils/textNoteUtils';

export interface SectionLabelProps extends Record<string, unknown> {
  classes?: SectionLabelPropsExternal['classes'];
  label: string;
  centered?: boolean;
}

export type SectionLabelNodeProps = NodeProps<Node<SectionLabelProps>>;

const LEFT_MARGIN = 16;

export function SectionLabelNode({
  id,
  data: { classes = {}, label, centered },
}: SectionLabelNodeProps) {
  const { getNode, getNodes } = useReactFlow();
  const { zoom } = useViewport();
  const node = getNode(id);
  const nodeWidth = node?.width ?? 0;
  const nodeX = node?.position.x ?? 0;

  const zoomFactor = 1 / zoom;
  const badgeTranslateY = '-11px'; // the badge height is rendered as 22px at 100%

  let badgePositionStyles: React.CSSProperties;

  if (centered) {
    // Center the badge over the diagram content rather than the viewport.
    // Text notes are excluded so dragging a sticky note doesn't shift the
    // centered label (e.g. "First Death" / "Second Death").
    const contentNodes = getNodes().filter(
      (n) => !isSectionLabelNode(n) && !isTextNoteNode(n) && !n.hidden
    );
    let contentOffset = 0;
    if (contentNodes.length > 0) {
      const xs = contentNodes.map((n) => n.position.x);
      const rights = contentNodes.map(
        (n) => n.position.x + (n.measured?.width ?? n.width ?? 0)
      );
      const contentCenterX = (Math.min(...xs) + Math.max(...rights)) / 2;
      const viewportCenterX = nodeX + nodeWidth / 2;
      contentOffset = contentCenterX - viewportCenterX;
    }
    badgePositionStyles = {
      display: 'flex',
      justifyContent: 'center',
      position: 'relative',
      left: contentOffset,
      willChange: 'transform',
      transform: `scale(${zoomFactor}) translateY(${badgeTranslateY})`,
      height: '100%',
    };
  } else {
    const anchorLabelOffset = (1 - zoom) / 2;
    const badgeTranslateX = nodeWidth * anchorLabelOffset + LEFT_MARGIN;
    badgePositionStyles = {
      willChange: 'transform',
      transform: `scale(${zoomFactor}) translateX(${badgeTranslateX}px) translateY(${badgeTranslateY})`,
      height: '100%',
    };
  }

  return (
    <SectionLabel
      zoom={zoom}
      label={label}
      classes={classes}
      styles={{
        // Scales only height, but leaves width untouched for full width styles
        hr: {
          willChange: 'transform',
          transform: `scale(1, ${zoomFactor}) translateY(-50%)`,
        },
        badgeWrapper: badgePositionStyles,
      }}
    />
  );
}
